/**
 * Goal Progress Modal - Refactored with MetricChart
 *
 * Displays goal progress over time using the modular chart system
 */

import React, { useState, useEffect } from 'react';
import { X, TrendingUp, Calendar } from 'lucide-react';
import { MetricChart, GoalChartConfig, ChartDataPoint } from '../../shared/charts';

interface Goal {
  goal_id: string;
  goal_name: string;
  goal_value: number;
  goal_type: 'over' | 'under' | 'target' | 'range' | 'boolean';
  variance_percent?: number;
  floor_value?: number;
  unit: string;
  health_data_field: string;
  category: string;
}

interface GoalHistoryPoint {
  date: string;
  actual: number | null;
  target: number;
  goal_type: string;
  variance: number;
  met_goal: boolean | null;
}

interface GoalProgressModalProps {
  goal: Goal;
  isOpen: boolean;
  onClose: () => void;
  userId?: string; // Optional userId for coach viewing client goals
}

type DateRange = '7' | '30' | 'custom';

export const GoalProgressModal: React.FC<GoalProgressModalProps> = ({ goal, isOpen, onClose, userId }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [historyData, setHistoryData] = useState<GoalHistoryPoint[]>([]);
  const [dateRange, setDateRange] = useState<DateRange>('7');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  useEffect(() => {
    if (isOpen) {
      fetchGoalHistory();
    }
  }, [isOpen, dateRange, customStartDate, customEndDate]);

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation(); // Prevent parent handlers from catching this
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen, onClose]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('auth_token');
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
  };

  const fetchGoalHistory = async () => {
    try {
      setLoading(true);
      setError(null);

      const days = dateRange === '7' ? 7 : dateRange === '30' ? 30 : undefined;
      let url = `/api/goals/${goal.goal_id}/history`;

      if (dateRange === 'custom' && customStartDate && customEndDate) {
        url += `?startDate=${customStartDate}&endDate=${customEndDate}`;
      } else if (days) {
        url += `?days=${days}`;
      }

      const headers = getAuthHeaders();
      // If userId is provided (coach viewing client), add it to headers
      if (userId) {
        headers['x-user-id'] = userId;
      }

      const response = await fetch(url, {
        headers
      });

      if (response.ok) {
        const data = await response.json();
        setHistoryData(data.history || []);
      } else {
        throw new Error('Failed to fetch goal history');
      }
    } catch (err) {
      console.error('Error fetching goal history:', err);
      setError('Failed to load goal progress data');
    } finally {
      setLoading(false);
    }
  };

  // Transform history data to chart format
  const transformHistoryToChartData = (): ChartDataPoint[] => {
    return historyData.map((point, index) => {
      // Detect if this is a missing day (no data)
      const isMissingData = point.actual === null || point.actual === undefined;

      // Mark as gap segment if previous day had data but this day doesn't
      // This will make the line segment connecting to this point yellow
      const prevPoint = index > 0 ? historyData[index - 1] : null;
      const prevHadData = prevPoint && prevPoint.actual !== null && prevPoint.actual !== undefined;
      const isGapSegment = isMissingData && prevHadData;

      return {
        label: point.date,
        value: point.actual,
        target: point.target,
        metTarget: point.met_goal,
        variance: point.variance,
        metadata: {
          goalType: point.goal_type,
          isGapSegment: isGapSegment
        }
      };
    });
  };

  // Determine appropriate chart type based on goal characteristics
  const getChartType = (): 'line' | 'bar' => {
    // Use bar charts for discrete daily metrics (steps, water, workouts)
    const barChartFields = ['steps', 'water', 'workouts'];
    if (barChartFields.some(field => goal.health_data_field.includes(field))) {
      return 'bar';
    }

    // Use line charts for continuous/target-based metrics (calories, weight, sleep, energy, stress)
    // Line charts better visualize staying within variance ranges
    return 'line';
  };

  // Build chart configuration
  const buildChartConfig = (): GoalChartConfig => {
    return {
      type: getChartType(),
      goalType: goal.goal_type,
      goalValue: goal.goal_value,
      variancePercent: goal.variance_percent,
      floorValue: goal.floor_value,
      unit: goal.unit,
      showTargetLine: true,
      showVarianceBand: true,
      datasets: [
        {
          label: `${goal.goal_name} (Actual)`,
          data: transformHistoryToChartData()
        }
      ],
      loading,
      error,
      emptyMessage: 'No progress data available for this date range',
      height: 400,
      responsive: true,
      maintainAspectRatio: false
    };
  };

  if (!isOpen) return null;

  return (
    <div className="goal-progress-modal">
      <div className="modal-overlay" onClick={onClose} />
      <div className="modal-content">
        <div className="modal-header">
          <h2>
            <TrendingUp className="w-6 h-6" />
            {goal.goal_name} - Progress Tracker
          </h2>
          <button onClick={onClose} className="close-btn">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="modal-body">
          {/* Date Range Selector */}
          <div className="date-range-selector">
            <div className="range-buttons">
              <button
                className={`range-btn ${dateRange === '7' ? 'active' : ''}`}
                onClick={() => setDateRange('7')}
              >
                Last 7 Days
              </button>
              <button
                className={`range-btn ${dateRange === '30' ? 'active' : ''}`}
                onClick={() => setDateRange('30')}
              >
                Last 30 Days
              </button>
              <button
                className={`range-btn ${dateRange === 'custom' ? 'active' : ''}`}
                onClick={() => setDateRange('custom')}
              >
                <Calendar className="w-4 h-4" />
                Custom Range
              </button>
            </div>

            {dateRange === 'custom' && (
              <div className="custom-date-inputs">
                <div className="date-input-group">
                  <label>Start Date</label>
                  <input
                    type="date"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                  />
                </div>
                <div className="date-input-group">
                  <label>End Date</label>
                  <input
                    type="date"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Chart Area - Now using MetricChart! */}
          <div className="chart-area">
            <MetricChart config={buildChartConfig()} />
          </div>

          {/* Goal Info */}
          <div className="goal-info-footer">
            <div className="info-item">
              <span className="label">Goal Type:</span>
              <span className="value">{goal.goal_type.toUpperCase()}</span>
            </div>
            <div className="info-item">
              <span className="label">Target:</span>
              <span className="value">{goal.goal_value} {goal.unit}</span>
            </div>
            {goal.goal_type === 'target' && goal.variance_percent && (
              <div className="info-item">
                <span className="label">Variance:</span>
                <span className="value">±{goal.variance_percent}%</span>
              </div>
            )}
            <div className="info-item">
              <span className="label">Category:</span>
              <span className="value">{goal.category}</span>
            </div>
          </div>
        </div>
      </div>

      <style>{styles}</style>
    </div>
  );
};

const styles = `
  .goal-progress-modal {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1000;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .modal-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.7);
    backdrop-filter: blur(4px);
    z-index: 1;
  }

  .modal-content {
    position: relative;
    width: 95vw;
    max-width: 1000px;
    max-height: 90vh;
    background: var(--bg-primary);
    border-radius: var(--radius-large);
    border: 1px solid var(--border-primary);
    box-shadow: var(--shadow-heavy);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    z-index: 2;
  }

  .modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 24px;
    border-bottom: 1px solid var(--border-primary);
    background: var(--bg-primary);
  }

  .modal-header h2 {
    display: flex;
    align-items: center;
    gap: 12px;
    margin: 0;
    font-size: 24px;
    font-weight: 600;
    color: var(--text-primary);
  }

  .close-btn {
    padding: 8px;
    background: none;
    border: none;
    border-radius: var(--radius-small);
    color: var(--text-secondary);
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .close-btn:hover {
    background: var(--bg-secondary);
    color: var(--text-primary);
  }

  .modal-body {
    flex: 1;
    overflow-y: auto;
    padding: 24px;
    display: flex;
    flex-direction: column;
    gap: 24px;
  }

  .date-range-selector {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .range-buttons {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
  }

  .range-btn {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 20px;
    background: var(--bg-secondary);
    color: var(--text-primary);
    border: 1px solid var(--border-primary);
    border-radius: var(--radius-medium);
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .range-btn:hover {
    background: var(--bg-tertiary);
    border-color: var(--border-secondary);
  }

  .range-btn.active {
    background: var(--accent-blue);
    color: white;
    border-color: var(--accent-blue);
  }

  .custom-date-inputs {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 16px;
  }

  .date-input-group {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .date-input-group label {
    font-size: 14px;
    font-weight: 500;
    color: var(--text-primary);
  }

  .date-input-group input {
    padding: 10px 12px;
    background: var(--bg-secondary);
    color: var(--text-primary);
    border: 1px solid var(--border-primary);
    border-radius: var(--radius-medium);
    font-size: 14px;
    transition: all 0.2s ease;
  }

  .date-input-group input:focus {
    outline: none;
    border-color: var(--accent-blue);
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  .chart-area {
    flex: 1;
    min-height: 400px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .goal-info-footer {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 16px;
    padding: 20px;
    background: var(--bg-secondary);
    border-radius: var(--radius-medium);
    border: 1px solid var(--border-primary);
  }

  .info-item {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .info-item .label {
    font-size: 12px;
    font-weight: 500;
    color: var(--text-secondary);
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .info-item .value {
    font-size: 16px;
    font-weight: 600;
    color: var(--text-primary);
  }

  @media (max-width: 768px) {
    .modal-content {
      width: 98vw;
      max-height: 95vh;
    }

    .modal-header,
    .modal-body {
      padding: 16px;
    }

    .range-buttons {
      flex-direction: column;
    }

    .goal-info-footer {
      grid-template-columns: 1fr;
    }
  }
`;
